import { type ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/auth';
import { useMinLoading } from '@/hooks/use-min-loading';
import { useSelectedMember } from '@/hooks/use-selected-member';
import { Fade } from '@/components/ui/fade';
// Task 31.6.2: admin click-through to the shared detail modal — documented exception.
// eslint-disable-next-line no-restricted-imports
import { ApproacherDetailModal } from '@/modules/admin/components/approacher-detail-modal';
import { useEarningsPageData } from '../hooks/use-earnings-page-data';
import { EarningsSkeleton } from './earnings-skeleton';
import { EarningsContent } from './earnings-content';
import { RaceBonusPopup } from './race-bonus-popup';

function buildContentProps(d: ReturnType<typeof useEarningsPageData>) {
  const s = d.summary.data!;
  const dr = d.derived!;
  return {
    summary: s,
    baseEarnings: dr.baseEarnings,
    avgPerDay: dr.avgPerDay,
    tier: dr.currentTier,
    daily: d.daily.data,
    payments: d.history.data ?? [],
    rankPosition: dr.rankPosition,
    creatorsToNext: dr.creatorsToNext,
    ranking: d.ranking.data ?? [],
    periodCreators: dr.periodCreators,
  } as const;
}

function ReadyContent({
  d,
}: {
  d: ReturnType<typeof useEarningsPageData>;
}): ReactNode {
  return <EarningsContent {...buildContentProps(d)} />;
}

function AdminMemberModal(): ReactNode {
  const { selectedId, setSelectedId } = useSelectedMember();
  return (
    <ApproacherDetailModal
      approacherId={selectedId}
      onClose={() => setSelectedId(null)}
    />
  );
}

export function EarningsPage(): ReactNode {
  const { user, isAdmin } = useAuth();
  const authIsLoading = useAuthStore((s) => s.isLoading);
  const d = useEarningsPageData(user?.id);
  const showSkeleton = useMinLoading(d.isLoading);
  const ready =
    !authIsLoading && !showSkeleton && !!d.summary.data && !!d.derived;
  return (
    <>
      <RaceBonusPopup />
      <Fade activeKey={ready ? 'data' : 'loading'}>
        {ready ? <ReadyContent d={d} /> : <EarningsSkeleton />}
      </Fade>
      {isAdmin && <AdminMemberModal />}
    </>
  );
}
